import { useEffect, useState } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import {
    Sun,
    Moon,
    Barbell,
    UserCircle,
    List,
    X,
} from '@phosphor-icons/react';
import { useTheme } from './ThemeProvider';
import { useAuth } from '@/contexts/AuthContext';
import { HEADER } from '@/constants/testIds';

function buildNavItems(isAuthed) {
    const base = [
        { to: '/', label: 'Home', end: true, testId: HEADER.homeLink },
        {
            to: '/style-guide',
            label: 'Style Guide',
            testId: HEADER.styleGuideLink,
        },
    ];
    if (!isAuthed) return base;
    return [
        ...base,
        { to: '/workouts', label: 'Workouts', testId: 'app-header-workouts-link' },
        { to: '/sessions', label: 'History', testId: 'app-header-history-link' },
        { to: '/insights', label: 'Insights', testId: 'app-header-insights-link' },
        { to: '/account', label: 'Account', testId: 'app-header-account-link' },
    ];
}

export function Header() {
    const { theme, toggleTheme } = useTheme();
    const { user, loading } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [menuOpen, setMenuOpen] = useState(false);
    const isDark = theme === 'dark';
    const isAuthed = !!user && user !== false;
    const navItems = buildNavItems(isAuthed);

    // Close menu whenever the route changes.
    useEffect(() => {
        setMenuOpen(false);
    }, [location.pathname]);

    // Close on Escape; lock body scroll while open.
    useEffect(() => {
        if (!menuOpen) return undefined;
        const onKey = (e) => {
            if (e.key === 'Escape') setMenuOpen(false);
        };
        document.addEventListener('keydown', onKey);
        const prev = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            document.removeEventListener('keydown', onKey);
            document.body.style.overflow = prev;
        };
    }, [menuOpen]);

    const navClass = ({ isActive }) =>
        `uppercase-mono px-3 py-2 rounded-full transition-colors ${
            isActive
                ? 'text-foreground bg-foreground/5'
                : 'text-muted-foreground hover:text-foreground'
        }`;

    const mobileItemClass = ({ isActive }) =>
        `flex items-center justify-between uppercase-mono text-sm tracking-wider px-4 py-3 rounded-xl transition-colors ${
            isActive
                ? 'text-foreground bg-foreground/10'
                : 'text-muted-foreground hover:text-foreground hover:bg-foreground/5'
        }`;

    return (
        <header data-testid={HEADER.root} className="sticky top-0 z-40 w-full">
            <div className="mx-auto max-w-6xl px-5 sm:px-8 py-4">
                <div className="glass-card rounded-full flex items-center justify-between pl-3 pr-2 py-2 gap-2">
                    <Link
                        to="/"
                        data-testid={HEADER.logo}
                        className="flex items-center gap-2 group shrink-0"
                    >
                        <span className="grid place-items-center w-9 h-9 rounded-full bg-foreground/5 border border-foreground/10 group-hover:bg-foreground/10 transition-colors">
                            <Barbell
                                size={18}
                                weight="bold"
                                className="text-foreground"
                            />
                        </span>
                        <span className="font-display font-extrabold tracking-tight text-base">
                            GYM
                            <span className="text-[#00FF9D] dark:text-[#00FF9D]">
                                FLOW
                            </span>
                        </span>
                    </Link>

                    <nav className="hidden sm:flex items-center gap-1">
                        {navItems.map((item) => (
                            <NavLink
                                key={item.to}
                                to={item.to}
                                end={item.end}
                                className={navClass}
                                data-testid={item.testId}
                            >
                                {item.label}
                            </NavLink>
                        ))}
                    </nav>

                    <div className="flex items-center gap-2 shrink-0">
                        {!loading && !isAuthed && (
                            <button
                                type="button"
                                onClick={() => navigate('/login')}
                                data-testid="app-header-signin-btn"
                                className="hidden sm:inline-flex items-center gap-2 h-10 px-4 rounded-full bg-primary text-primary-foreground font-bold tracking-tight text-sm shadow-neon hover:-translate-y-[2px] active:scale-[0.97] transition-all"
                            >
                                Sign in
                            </button>
                        )}
                        {!loading && isAuthed && (
                            <button
                                type="button"
                                onClick={() => navigate('/account')}
                                data-testid="app-header-account-btn"
                                className="hidden sm:inline-flex items-center gap-2 h-10 pl-2 pr-3 rounded-full border border-foreground/10 bg-foreground/5 hover:bg-foreground/10 active:scale-[0.97] transition-all text-sm font-semibold"
                                title={user.email}
                            >
                                {user.avatar ? (
                                    <img
                                        src={user.avatar}
                                        alt=""
                                        className="w-7 h-7 rounded-full object-cover"
                                    />
                                ) : (
                                    <span className="grid place-items-center w-7 h-7 rounded-full bg-foreground/10">
                                        <UserCircle
                                            size={16}
                                            weight="bold"
                                        />
                                    </span>
                                )}
                                <span className="max-w-[100px] truncate">
                                    {user.name?.split(' ')[0] || 'You'}
                                </span>
                            </button>
                        )}

                        <button
                            type="button"
                            onClick={toggleTheme}
                            data-testid={HEADER.themeToggle}
                            aria-label={
                                isDark
                                    ? 'Switch to light mode'
                                    : 'Switch to dark mode'
                            }
                            className="relative grid place-items-center w-10 h-10 rounded-full border border-foreground/10 bg-foreground/5 hover:bg-foreground/10 active:scale-[0.96] transition-all"
                        >
                            {isDark ? (
                                <Sun size={18} weight="bold" />
                            ) : (
                                <Moon size={18} weight="bold" />
                            )}
                        </button>

                        {/* Hamburger toggle — mobile only */}
                        <button
                            type="button"
                            onClick={() => setMenuOpen((v) => !v)}
                            data-testid={HEADER.mobileMenuToggle}
                            aria-label={menuOpen ? 'Close menu' : 'Open menu'}
                            aria-expanded={menuOpen}
                            aria-controls={HEADER.mobileMenu}
                            className="sm:hidden relative grid place-items-center w-10 h-10 rounded-full border border-foreground/10 bg-foreground/5 hover:bg-foreground/10 active:scale-[0.96] transition-all"
                        >
                            {menuOpen ? (
                                <X size={18} weight="bold" />
                            ) : (
                                <List size={18} weight="bold" />
                            )}
                        </button>
                    </div>
                </div>

                {/* Mobile menu sheet — slides down from the header */}
                {menuOpen && (
                    <>
                        {/* Backdrop */}
                        <button
                            type="button"
                            aria-label="Close menu"
                            onClick={() => setMenuOpen(false)}
                            className="sm:hidden fixed inset-0 top-[72px] z-30 bg-background/60 backdrop-blur-sm"
                        />

                        <div
                            id={HEADER.mobileMenu}
                            data-testid={HEADER.mobileMenu}
                            className="sm:hidden absolute left-5 right-5 mt-2 z-40 origin-top animate-fade-up"
                        >
                            <div className="glass-card rounded-2xl p-3 flex flex-col gap-1 shadow-tactile">
                                {navItems.map((item) => (
                                    <NavLink
                                        key={item.to}
                                        to={item.to}
                                        end={item.end}
                                        onClick={() => setMenuOpen(false)}
                                        className={mobileItemClass}
                                        data-testid={`mobile-${item.testId}`}
                                    >
                                        <span>{item.label}</span>
                                    </NavLink>
                                ))}

                                <div className="h-px bg-foreground/10 my-2" />

                                {!loading && !isAuthed && (
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setMenuOpen(false);
                                            navigate('/login');
                                        }}
                                        data-testid="mobile-app-header-signin-btn"
                                        className="inline-flex items-center justify-center gap-2 h-11 px-4 rounded-xl bg-primary text-primary-foreground font-bold tracking-tight text-sm shadow-neon active:scale-[0.98] transition-all"
                                    >
                                        Sign in
                                    </button>
                                )}
                                {!loading && isAuthed && (
                                    <div className="flex items-center gap-3 px-3 py-2 rounded-xl bg-foreground/5 border border-foreground/10">
                                        {user.avatar ? (
                                            <img
                                                src={user.avatar}
                                                alt=""
                                                className="w-9 h-9 rounded-full object-cover"
                                            />
                                        ) : (
                                            <span className="grid place-items-center w-9 h-9 rounded-full bg-foreground/10">
                                                <UserCircle
                                                    size={18}
                                                    weight="bold"
                                                />
                                            </span>
                                        )}
                                        <div className="min-w-0 flex-1">
                                            <p className="font-semibold text-sm truncate">
                                                {user.name || 'You'}
                                            </p>
                                            <p className="text-muted-foreground text-xs truncate">
                                                {user.email}
                                            </p>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </>
                )}
            </div>
        </header>
    );
}
